import { Button, Text, UnstyledButton } from '@mantine/core';
import { IconChevronDown, IconChevronRight } from '@tabler/icons-react';
import { useState } from 'react';
import { fieldFor, fieldNameFor, rootFor } from './responsePaths';
import classes from './workspace.module.css';

// Long lists show their first items only; every item has the same shape to pick from.
const SHOWN_ITEMS = 3;
const OPEN_DEPTH = 8;

const scalar = (value) => (typeof value === 'string' ? `"${value}"` : String(value));

const Pick = ({ on, children, ...props }) => (
  <Button size="compact-xs" variant={on ? 'filled' : 'default'} fz={10.5} {...props}>
    {children}
  </Button>
);

export const TreeNode = ({ name, value, segments, depth, picker }) => {
  const [open, setOpen] = useState(depth < OPEN_DEPTH);
  const list = Array.isArray(value);
  const branch = list || (value !== null && typeof value === 'object');
  const field = picker.fieldOf(segments);
  const isRow = field === '';
  const mapped = field ? picker.mappedAs(field) : null;
  const root = branch && !isRow ? picker.rootOf(segments, value) : null;
  const children = branch ? Object.entries(value) : [];
  const label = name === undefined ? 'response' : String(name);

  return (
    <div className={isRow ? classes.row : undefined}>
      <div className={classes.node}>
        {branch ? (
          <UnstyledButton aria-label={`${open ? 'Collapse' : 'Expand'} ${label}`} onClick={() => setOpen(!open)}>
            {open ? <IconChevronDown size={12} /> : <IconChevronRight size={12} />}
          </UnstyledButton>
        ) : (
          <span style={{ width: 12 }} />
        )}
        <span className={classes.key}>{label}</span>:
        {branch ? (
          <Text span size="xs" c="dimmed" ff="monospace">
            {list ? `[${value.length} item${value.length === 1 ? '' : 's'}]` : '{…}'}
          </Text>
        ) : (
          <span className={mapped ? classes.mapped : undefined}>{scalar(value)}</span>
        )}
        {root &&
          (picker.isRoot(root) ? (
            <Pick on>Rows ✓</Pick>
          ) : (
            <Pick aria-label={`Use ${label} as rows`} onClick={() => picker.useRoot(root)}>
              {root.rootKind === 'object' ? 'Use as one row' : 'Use as rows'}
            </Pick>
          ))}
        {field && !picker.readOnly && picker.slots && picker.slot && (
          <Pick aria-label={`Fill ${picker.slot} with ${field}`} onClick={() => picker.fillSlot(field)}>
            → {picker.slot}
          </Pick>
        )}
        {field && !picker.readOnly && !picker.slots && (
          <Pick
            on={!!mapped}
            aria-label={mapped ? `Remove field ${mapped}` : `Add ${field} as a field`}
            onClick={() => picker.toggleField(field)}
          >
            {mapped ? 'field ✓' : field === '$key' ? '+ key' : '+ field'}
          </Pick>
        )}
        {mapped && (
          <Text span size="xs" c="var(--mantine-primary-color-filled)" fw={700}>
            → {mapped}
          </Text>
        )}
      </div>
      {branch && open && (
        <div className={classes.children}>
          {children.slice(0, list ? SHOWN_ITEMS : undefined).map(([key, item]) => (
            <TreeNode
              key={key}
              name={list ? Number(key) : key}
              value={item}
              segments={[...segments, list ? Number(key) : key]}
              depth={depth + 1}
              picker={picker}
            />
          ))}
          {list && value.length > SHOWN_ITEMS && (
            <Text size="xs" c="dimmed">
              … {value.length - SHOWN_ITEMS} more
            </Text>
          )}
        </div>
      )}
    </div>
  );
};

// Clicks on the tree write the Rows step: the root (with [] for nested lists) and the field paths.
// With `slots` (a capability) a value click fills the selected slot instead of adding a field.
export const usePicker = (form, setField, body, readOnly, slots = null) => {
  const location = { root: form.root, rootKind: form.rootKind };
  const mappedAs = (path) => form.mapRows.find((row) => row.value.trim() === path)?.key.trim() || null;
  const setRoot = (next) => {
    setField('root', next.root);
    setField('rootKind', next.rootKind);
  };

  return {
    readOnly,
    slots: !!slots,
    slot: slots?.slot || null,
    fillSlot: (path) => slots?.fill(path),
    fieldOf: (segments) => fieldFor(location, segments, body),
    rootOf: (segments, value) =>
      readOnly ? null : rootFor(segments, value, { dictionary: form.rootKind === 'dictionary' }),
    isRoot: (next) => next.root === form.root.trim(),
    mappedAs,
    useRoot: setRoot,
    useRootExample: (root) => {
      const before = location;
      setRoot({ root, rootKind: 'array' });
      return () => setRoot(before);
    },
    toggleField: (path) => {
      if (mappedAs(path))
        setField(
          'mapRows',
          form.mapRows.filter((row) => row.value.trim() !== path)
        );
      else {
        const taken = form.mapRows.map((row) => row.key.trim());
        setField('mapRows', [...form.mapRows, { key: fieldNameFor(path, taken), value: path }]);
      }
    },
  };
};
